import {
  TICKET_UTM_MEDIUM,
  TICKET_EXPERIENCE_SESSION_KEY,
} from './ticket.constants';
import { TicketExperienceObj, TicketExperienceType } from './ticket.types';

const TICKET_EXPERIENCE_NAME: TicketExperienceType = 'ticket';

/**
 * Extracts Ticket experience type from URL parameters.
 * Checks for utm_medium=ticket to indicate the user came from a Ticket campaign.
 *
 * @returns {TicketExperienceType | null} The experience type or null if not a valid Ticket experience
 *
 * @example
 * // For URL: https://example.com/?utm_medium=ticket
 * const experience = getTicketExperienceFromURL();
 * // Returns: "ticket"
 */
const getTicketExperienceFromURL = (): TicketExperienceType | null => {
  const urlParams = new URLSearchParams(window.location.search);
  const utmMedium = urlParams.get('utm_medium');

  if (utmMedium !== TICKET_UTM_MEDIUM) return null;

  return TICKET_EXPERIENCE_NAME;
};

/**
 * Initializes and stores the Ticket experience in session storage.
 * Extracts the experience type from URL parameters and persists it for later use.
 *
 * @example
 * // Call this function when the page loads to capture Ticket experience
 * startTicketExperience();
 */
export const startTicketExperience = () => {
  const ticketExperience = getTicketExperienceFromURL();
  if (!ticketExperience) return;
  const ticketExpObj: TicketExperienceObj = {
    experience: ticketExperience,
  };

  sessionStorage.setItem(TICKET_EXPERIENCE_SESSION_KEY, JSON.stringify(ticketExpObj));
};

/**
 * Retrieves the current Ticket experience from URL or session storage.
 * First checks URL parameters, then falls back to session storage.
 *
 * @returns {TicketExperienceObj | null} The Ticket experience object or null if not found
 *
 * @example
 * const experience = getTicketExperience();
 * if (experience) {
 *   console.log('User experience:', experience.experience); // "ticket"
 * }
 */
export const getTicketExperience = (): TicketExperienceObj | null => {
  const experienceFromUrl = getTicketExperienceFromURL();
  if (experienceFromUrl) return { experience: experienceFromUrl };

  const sessionObj = sessionStorage.getItem(TICKET_EXPERIENCE_SESSION_KEY);
  if (sessionObj) return JSON.parse(sessionObj);

  return null;
};

export * from './ticket.types';
export * from './ticket.constants';
